import Papa from 'papaparse';

const MAX_BROWSER_PARSE_BYTES = 10 * 1024 * 1024;

const CPQ_BILLING_HEADER_ALIASES = {
  dealerCode: ['Dealer Code', 'DealerCode', 'BAC'],
  dealerName: ['Dealer Name', 'DealerName'],
  productName: ['Product Name'],
  oemProductCode: ['OEM Product Code'],
  budget: ['Budget'],
  budgetCurrency: ['Budget Currency'],
  provider: ['Provider'],
  providerCurrency: ['Provider Currency'],
  shift: ['Shift'],
  shiftCurrency: ['Shift Currency'],
  serviceMonth: ['Service Month'],
  quantity: ['Quantity'],
  usageType: ['Usage Type'],
  activationDate: ['Activation Date'],
  enrollmentDate: ['Enrollment Date'],
  invoiceNumber: ['Invoice #'],
  productStatus: ['Product Status'],
};

const GM_BILLING_HEADER_ALIASES = {
  bac: ['BAC'],
  isTerminated: ['Is Terminated'],
  isTerminatedDate: ['IsTerminatedDate', 'Is Terminated Date'],
  enrolledDate: ['Enrolled Date'],
  enrollmentStatus: ['Enrollment Status Code'],
  optOutDate: ['Opt Out Date'],
  lastUpdatedDate: ['Last Updated Date'],
  vendorName: ['Vendor Name'],
  vendorStatus: ['Vendor Status'],
  productDesignation: ['Product Designation'],
  productSelection: ['Product Selection'],
  productCode: ['Product Code'],
  productBrand: ['Product Brand'],
  productSelectionDate: ['Product Selection Date'],
  goLiveDate: ['Go Live Date'],
  cancelDate: ['Cancel Date'],
  productStatus: ['Product Status'],
  billingStartDate: ['Billing Start Date'],
  billingEndDate: ['Billing End Date'],
  isBilling: ['Is Billing'],
  dealerCost: ['Dealer Cost'],
  effectiveDate: ['Effective Date'],
  notes: ['Notes'],
};

const readValue = (row, aliases) => {
  for (const key of aliases) {
    if (row[key] !== undefined && row[key] !== null && row[key] !== '') return row[key];
  }
  return '';
};

const toNumber = (v) => {
  if (v === '' || v === null || v === undefined) return 0;
  const n = Number(String(v).replace(/[$,]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const toBool = (v) => {
  if (typeof v === 'boolean') return v;
  const s = String(v ?? '').trim().toLowerCase();
  return s === 'true' || s === 'y' || s === 'yes' || s === '1';
};

const str = (v) => String(v ?? '').trim();

const normalizeCpqBillingRow = (raw) => ({
  dealerCode: str(readValue(raw, CPQ_BILLING_HEADER_ALIASES.dealerCode)),
  dealerName: str(readValue(raw, CPQ_BILLING_HEADER_ALIASES.dealerName)),
  productName: str(readValue(raw, CPQ_BILLING_HEADER_ALIASES.productName)),
  oemProductCode: str(readValue(raw, CPQ_BILLING_HEADER_ALIASES.oemProductCode)),
  budget: toNumber(readValue(raw, CPQ_BILLING_HEADER_ALIASES.budget)),
  budgetCurrency: str(readValue(raw, CPQ_BILLING_HEADER_ALIASES.budgetCurrency)),
  provider: toNumber(readValue(raw, CPQ_BILLING_HEADER_ALIASES.provider)),
  providerCurrency: str(readValue(raw, CPQ_BILLING_HEADER_ALIASES.providerCurrency)),
  shift: toNumber(readValue(raw, CPQ_BILLING_HEADER_ALIASES.shift)),
  shiftCurrency: str(readValue(raw, CPQ_BILLING_HEADER_ALIASES.shiftCurrency)),
  serviceMonth: str(readValue(raw, CPQ_BILLING_HEADER_ALIASES.serviceMonth)).toUpperCase(),
  quantity: toNumber(readValue(raw, CPQ_BILLING_HEADER_ALIASES.quantity)),
  usageType: str(readValue(raw, CPQ_BILLING_HEADER_ALIASES.usageType)),
  activationDate: str(readValue(raw, CPQ_BILLING_HEADER_ALIASES.activationDate)),
  enrollmentDate: str(readValue(raw, CPQ_BILLING_HEADER_ALIASES.enrollmentDate)),
  invoiceNumber: str(readValue(raw, CPQ_BILLING_HEADER_ALIASES.invoiceNumber)),
  productStatus: str(readValue(raw, CPQ_BILLING_HEADER_ALIASES.productStatus)).toUpperCase(),
});

const normalizeGmBillingRow = (raw) => ({
  bac: str(readValue(raw, GM_BILLING_HEADER_ALIASES.bac)),
  isTerminated: str(readValue(raw, GM_BILLING_HEADER_ALIASES.isTerminated)),
  isTerminatedDate: str(readValue(raw, GM_BILLING_HEADER_ALIASES.isTerminatedDate)),
  enrolledDate: str(readValue(raw, GM_BILLING_HEADER_ALIASES.enrolledDate)),
  enrollmentStatus: str(readValue(raw, GM_BILLING_HEADER_ALIASES.enrollmentStatus)),
  optOutDate: str(readValue(raw, GM_BILLING_HEADER_ALIASES.optOutDate)),
  lastUpdatedDate: str(readValue(raw, GM_BILLING_HEADER_ALIASES.lastUpdatedDate)),
  vendorName: str(readValue(raw, GM_BILLING_HEADER_ALIASES.vendorName)),
  vendorStatus: str(readValue(raw, GM_BILLING_HEADER_ALIASES.vendorStatus)),
  productDesignation: str(readValue(raw, GM_BILLING_HEADER_ALIASES.productDesignation)),
  productSelection: str(readValue(raw, GM_BILLING_HEADER_ALIASES.productSelection)),
  productCode: str(readValue(raw, GM_BILLING_HEADER_ALIASES.productCode)),
  productBrand: str(readValue(raw, GM_BILLING_HEADER_ALIASES.productBrand)),
  productSelectionDate: str(readValue(raw, GM_BILLING_HEADER_ALIASES.productSelectionDate)),
  goLiveDate: str(readValue(raw, GM_BILLING_HEADER_ALIASES.goLiveDate)),
  cancelDate: str(readValue(raw, GM_BILLING_HEADER_ALIASES.cancelDate)),
  productStatus: str(readValue(raw, GM_BILLING_HEADER_ALIASES.productStatus)),
  billingStartDate: str(readValue(raw, GM_BILLING_HEADER_ALIASES.billingStartDate)),
  billingEndDate: str(readValue(raw, GM_BILLING_HEADER_ALIASES.billingEndDate)),
  isBilling: toBool(readValue(raw, GM_BILLING_HEADER_ALIASES.isBilling)),
  dealerCost: toNumber(readValue(raw, GM_BILLING_HEADER_ALIASES.dealerCost)),
  effectiveDate: str(readValue(raw, GM_BILLING_HEADER_ALIASES.effectiveDate)),
  notes: str(readValue(raw, GM_BILLING_HEADER_ALIASES.notes)),
});

const normalizeHeader = (header) => String(header ?? '').replace(/^\ufeff/, '').trim();

const parseCsvGrid = (text) => new Promise((resolve, reject) => {
  Papa.parse(text, {
    header: false,
    skipEmptyLines: true,
    complete: (res) => resolve(res.data),
    error: reject,
  });
});

const rowHasColumns = (row = [], columns = []) => {
  const headers = new Set(row.map(normalizeHeader));
  return columns.every((column) => headers.has(column));
};

const findHeaderIndex = (rows, source) => rows.findIndex((row) => (
  source === 'gm'
    ? rowHasColumns(row, ['BAC', 'Product Code'])
    : rowHasColumns(row, ['Dealer Code', 'Product Name', 'Budget'])
));

const rowsToObjects = (rows, source) => {
  const headerIndex = findHeaderIndex(rows, source);
  if (headerIndex < 0) return [];
  const header = rows[headerIndex].map(normalizeHeader);
  return rows.slice(headerIndex + 1).map((values) => (
    Object.fromEntries(header.map((column, index) => [column, values[index] ?? '']))
  ));
};

/**
 * Parse an uploaded File (CSV/TSV/text) into an array of rows normalized to
 * either the GM shape or the DI CPQ shape, based on `source`.
 *
 * @param {File} file - user-selected File
 * @param {'gm'|'cpq'} source - which schema to normalize to
 * @returns {Promise<{ rows: object[], rawCount: number, fileName: string }>}
 */
export const parseUploadedFile = async (file, source) => {
  const fileName = file.name;
  const ext = fileName.split('.').pop().toLowerCase();
  if ((ext === 'csv' || ext === 'tsv' || ext === 'txt') && Number(file.size || 0) > MAX_BROWSER_PARSE_BYTES) {
    throw new Error('Billing reconciliation uploads are limited to 10 MB. Use a smaller CSV/TXT file or upload the XLSX workbook for server-side parsing.');
  }
  let rawRows;
  if (ext === 'csv' || ext === 'tsv' || ext === 'txt') {
    const text = await file.text();
    rawRows = rowsToObjects(await parseCsvGrid(text), source);
  } else if (ext === 'xlsx') {
    return { rows: [], rawCount: 0, fileName, serverParsed: true };
  } else {
    throw new Error(`Unsupported file type: .${ext}`);
  }
  const normalize = source === 'gm' ? normalizeGmBillingRow : normalizeCpqBillingRow;
  const rows = rawRows
    .map(normalize)
    .filter((r) => (source === 'gm' ? r.bac : r.dealerCode));
  return { rows, rawCount: rawRows.length, fileName };
};
